import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import {
  formSchema,
  CreateStoreFormData,
  basicInfoSchema,
  legalFiscalSchema,
  b2bSchema,
} from "./createStoreSchema";
import { useTranslation } from "react-i18next";

export function useCreateStoreWizard(
  onClose: () => void,
  onComplete?: (data: CreateStoreFormData & { address: string }) => void
) {
  const [step, setStep] = useState(0);
  const { t } = useTranslation();

  const steps = [t("stepper.step1"), t("stepper.step2"), t("stepper.step3")];

  const form = useForm<CreateStoreFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      legalName: "",
      address: "",
      street: "",
      streetNumber: "",
      city: "",
      zip: "",
      state: "",
      phone: "",
      email: "",
      companyPhone: "",
      taxId: "",
      businessType: "gmbh",
      registrationNumber: "",
      taxOffice: "",
      hasVatExemption: false,
      acceptTerms: false,
      isB2B: true,
      industryType: "other",
      taxExemptionCertificate: false,
      gdprCompliance: false,
    },
    mode: "onChange",
  });

  const nextStep = async () => {
    let fieldsToValidate: string[] = [];

    if (step === 0) {
      fieldsToValidate = Object.keys(basicInfoSchema.shape);
    } else if (step === 1) {
      fieldsToValidate = Object.keys(legalFiscalSchema.shape);
    } else if (step === 2) {
      fieldsToValidate = Object.keys(b2bSchema.shape);
    }

    const result = await form.trigger(fieldsToValidate as any, {
      shouldFocus: true,
    });

    if (result) {
      if (step < steps.length - 1) {
        setStep(step + 1);
      } else {
        // Get all form values
        const values = form.getValues();

        // Create a properly formatted address string
        const formattedAddress = [
          values.street,
          values.streetNumber,
          values.zip,
          values.city,
          values.state || "Germany",
        ]
          .filter(Boolean)
          .join(", ");

        // Call onClose with the complete form data including formatted address
        onClose();

        if (onComplete) {
          onComplete({
            ...values,
            address: formattedAddress,
            street: values.street,
            streetNumber: values.streetNumber,
          });
        }
      }
    }
  };

  const prevStep = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  return {
    form,
    step,
    steps,
    nextStep,
    prevStep,
  };
}
